import { Link } from '@inertiajs/react'
import type { ReactNode } from 'react'
import Layout from '@/components/Layout'
import {
  HeritageBadge,
  TrustStrip,
  PrimaryButton,
  GhostButton,
  Eyebrow,
} from '@/components/atoms'
import { BreadcrumbJsonLd } from '@/components/shared/json-ld'
import {
  CO_TAX_CREDIT,
  CO_TAX_CREDIT_URL,
  CO_REBATE_BASE,
  CO_REBATE_CARGO_BONUS,
  CO_REBATE_ADAPTIVE_BONUS,
  CO_MAX_CARGO_STACK,
  CO_REBATE_URL,
  CO_REBATE_PORTAL_URL,
} from '@/lib/constants'
import { resolveRebateWindow, formatWindowDate } from '@/lib/rebateWindow'

// ---------------------------------------------------------------------------
// SOURCES — every number and rule on this page is quoted from a state page
// that was fetched and read on 2026-08-06. Nothing here is inferred.
//
//   https://energyoffice.colorado.gov/ebike-rebates
//     · "Base Incentive Amount $1,000 / E-Cargo Bike Incentive +$300 /
//        Adaptive E-Bike Incentive +$300"
//     · Registration windows: Aug 24–31 2026, Oct 19–26 2026,
//       Jan 11–18 2027, Mar 15–22 2027. Open 11:00 AM MT, close 12:00 PM MT.
//     · "The Colorado Energy Office will randomly select registrants"
//     · "upload your required proof of Colorado address and income
//        eligibility documents within one week"
//     · "redeem your rebate at a participating retailer within sixty
//        calendar days"
//     · "Only one lifetime e-bike rebate is allowed per person."
//     · Income: below 80% AMI for your county, OR enrollment in CARE, WAP,
//       Medicaid, RTD LiVE, Section 8, SSDI, WIC, LEAP, SNAP, or SSI.
//     · E-cargo eligibility: extended frame (long tail, long john, Bakfiet,
//       or box bike) with published cargo capacity of at least 100 lbs.
//       No maximum purchase price for e-cargo (standard e-bikes cap at $5,000).
//     · All bikes: UL 2849 or EN 15194 certified, new, motor 750W or less.
//
//   https://energyoffice.colorado.gov/ebike-tax-credit
//     · "$225 discount per e-bike (limited to one discount a year per
//        resident)"
//     · "The Colorado E-Bike Tax Credit Discount can be combined with a
//        Colorado E-Bike Rebate at retailers participating in both programs."
//
//   Participating-retailer lists (Front Range Cargo Bikes confirmed on BOTH,
//   2026-08-06): rebate list gid=0 of spreadsheet 1cwV2k6QY25… and tax-credit
//   list 1VTb1sOUb5He3i…
//
// DO NOT add a number to this page that is not in one of those sources.
// ---------------------------------------------------------------------------

const eligibilityPrograms = [
  'SNAP', 'Medicaid', 'LEAP', 'WIC', 'SSI', 'SSDI',
  'Section 8', 'RTD LiVE', 'CARE', 'WAP',
]

const steps = [
  {
    hand: 'step 1',
    t: 'Register during the window',
    d: 'Name and contact info only. It takes about two minutes and costs nothing. You are not applying yet — you are entering the draw.',
  },
  {
    hand: 'step 2',
    t: 'The state draws at random',
    d: 'Colorado Energy Office picks registrants randomly and emails the ones selected. Registering early in the window does not improve your odds.',
  },
  {
    hand: 'step 3',
    t: 'Send proof within one week',
    d: 'If selected, you upload proof of Colorado address and income eligibility. Miss the week and the rebate goes to someone else.',
  },
  {
    hand: 'step 4',
    t: 'Redeem with us within 60 days',
    d: 'Come in and pick the bike. We are on the state participating-retailer list for both programs, so your rebate can be redeemed here.',
  },
]

const faqItems = [
  {
    q: 'Do I have to buy the bike to register?',
    a: 'No. Registration is free, takes two minutes, and commits you to nothing. If you are not selected you have lost nothing but the two minutes.',
  },
  {
    q: 'What if I am over the income limit?',
    a: `You can still take the $${CO_TAX_CREDIT} Colorado tax credit discount off any qualifying e-bike, with no income test and no lottery. We apply it at the counter.`,
  },
  {
    q: 'Can I combine the rebate and the tax credit?',
    a: `Yes, at retailers participating in both programs — we are on both state lists. That is $${CO_REBATE_BASE.toLocaleString()} + $${CO_REBATE_CARGO_BONUS} + $${CO_TAX_CREDIT} = $${CO_MAX_CARGO_STACK.toLocaleString()} off a qualifying cargo bike.`,
  },
  {
    q: 'Does a cargo bike actually qualify for the extra $300?',
    a: 'It has to have an extended frame — long tail, long john, Bakfiet, or box bike — with a published cargo capacity of at least 100 lbs. Ask us about the specific bike you want and we will check its published capacity with you before you commit.',
  },
  {
    q: 'Is there a price cap?',
    a: 'Standard e-bikes cap at $5,000. E-cargo bikes have no minimum or maximum purchase price, which is why the cargo category is the better deal under this program.',
  },
  {
    q: 'How many times can I get the rebate?',
    a: 'Once. It is one lifetime e-bike rebate per person. The $225 tax credit discount is separate and resets every calendar year.',
  },
]

function Rebate() {
  const window = resolveRebateWindow()

  const windowLine =
    window.status === 'open'
      ? `Registration is OPEN — closes ${formatWindowDate(window.closes)} at 12:00 PM MT`
      : window.status === 'upcoming'
        ? `Registration opens ${formatWindowDate(window.opens)} at 11:00 AM MT — ${window.daysUntil} ${window.daysUntil === 1 ? 'day' : 'days'} out`
        : 'The state has not published the next registration window yet'

  return (
    <>
      <BreadcrumbJsonLd items={[{ name: 'Colorado E-Bike Rebate', href: '/rebate' }]} />

      {/* ── Hero ── */}
      <section className="bg-[var(--background-warm)] py-16 pb-20 overflow-hidden relative">
        <svg
          aria-hidden="true"
          className="absolute inset-0 w-full h-full pointer-events-none"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 1200 600"
          preserveAspectRatio="xMidYMid slice"
        >
          <path d="M0 120 Q200 80 400 140 T800 100 T1200 130" fill="none" stroke="var(--primary)" strokeWidth="1.2" opacity="0.14" />
          <path d="M0 240 Q300 200 600 260 T1200 220" fill="none" stroke="var(--primary)" strokeWidth="0.6" opacity="0.14" />
          <path d="M0 360 Q250 320 500 380 T1000 340 T1200 370" fill="none" stroke="var(--primary)" strokeWidth="1.2" opacity="0.14" />
          <path d="M0 480 Q350 440 700 500 T1200 460" fill="none" stroke="var(--primary)" strokeWidth="0.6" opacity="0.14" />
        </svg>

        <div className="container relative">
          <div className="max-w-3xl">
            {/* HeritageBadge is whitespace-nowrap by design, so it can only hold
                a short string — the full window sentence overflows a 390px
                viewport. Badge carries the status; the sentence sits below it. */}
            <HeritageBadge>Colorado E-Bike Rebate · 2026</HeritageBadge>

            <p className="mt-4 text-sm font-semibold uppercase tracking-[0.08em] text-accent">
              {windowLine}
            </p>

            <h1 className="mt-5 text-4xl md:text-6xl font-bold tracking-tight text-foreground leading-[1.05]">
              Up to{' '}
              <span className="text-accent">${CO_MAX_CARGO_STACK.toLocaleString()}</span>{' '}
              off a cargo bike.{' '}
              <span className="hand text-[64px] md:text-[88px] text-primary">if you register.</span>
            </h1>

            <p className="mt-6 text-lg text-foreground leading-relaxed max-w-2xl">
              Colorado&apos;s income-qualified e-bike rebate is back. It is{' '}
              ${CO_REBATE_BASE.toLocaleString()} base, plus ${CO_REBATE_CARGO_BONUS} more
              for a qualifying e-cargo bike, and it stacks with the ${CO_TAX_CREDIT} state
              tax-credit discount. We are on the state&apos;s participating-retailer list
              for both.
            </p>

            <p className="mt-4 text-lg font-semibold text-primary max-w-2xl">
              It is a lottery, and you have to be registered before the window closes to
              be in it. Registering is free and takes two minutes.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <a href={CO_REBATE_PORTAL_URL} target="_blank" rel="noopener noreferrer">
                <PrimaryButton
                  size="lg"
                  hand={window.status === 'open' ? 'Register with the state' : 'Go to the state portal'}
                  sub="Opens coloradoebikerebates.aptracks.com · free"
                />
              </a>
              <Link href="/bikes">
                <GhostButton size="lg" sub="See what qualifies for the +$300 cargo bonus">
                  Browse cargo bikes →
                </GhostButton>
              </Link>
            </div>

            <p className="mt-5 text-sm text-muted-foreground max-w-2xl">
              We do not run this program and we cannot register you for it — the state
              does that.{' '}
              <a
                href={CO_REBATE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="underline text-primary"
              >
                Read the official rules at the Colorado Energy Office
              </a>
              . What we can do is help you figure out which bike you want in the
              meantime, and take the rebate when it lands.
            </p>
          </div>
        </div>
      </section>

      <TrustStrip
        items={[
          'On the state retailer list',
          'Both programs stack here',
          { label: 'Boulder · 47th Street', href: '/contact' },
        ]}
      />

      {/* ── The stack ── */}
      <section className="bg-[var(--background)] py-20">
        <div className="container">
          <Eyebrow className="text-accent">The math · what actually comes off</Eyebrow>
          <h2 className="mt-3 text-3xl md:text-4xl font-bold text-foreground">
            Two separate programs,{' '}
            <span className="hand text-[52px] text-primary">one counter.</span>
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-[22px] mt-9">
            <div className="bg-card border border-border rounded-[var(--radius)] p-7 shadow-[var(--shadow-sm)]">
              <div className="hand text-[52px] text-accent leading-none mb-3">
                ${CO_REBATE_BASE.toLocaleString()}
              </div>
              <h3 className="text-xl font-semibold text-foreground mb-2">Base rebate</h3>
              <p className="text-sm text-foreground leading-relaxed">
                Income-qualified, lottery-selected. One per person, ever. This is the
                piece you have to register for.
              </p>
            </div>

            <div className="bg-card border border-border rounded-[var(--radius)] p-7 shadow-[var(--shadow-sm)]">
              <div className="hand text-[52px] text-accent leading-none mb-3">
                +${CO_REBATE_CARGO_BONUS}
              </div>
              <h3 className="text-xl font-semibold text-foreground mb-2">E-cargo bonus</h3>
              <p className="text-sm text-foreground leading-relaxed">
                For an extended-frame bike rated to carry at least 100 lbs. Adaptive
                e-bikes get the same +${CO_REBATE_ADAPTIVE_BONUS}. This is our whole floor.
              </p>
            </div>

            <div className="bg-card border border-border rounded-[var(--radius)] p-7 shadow-[var(--shadow-sm)]">
              <div className="hand text-[52px] text-accent leading-none mb-3">
                +${CO_TAX_CREDIT}
              </div>
              <h3 className="text-xl font-semibold text-foreground mb-2">Tax credit discount</h3>
              <p className="text-sm text-foreground leading-relaxed">
                No income test, no lottery, no application. Any Colorado resident, once
                per calendar year.{' '}
                <Link href="/tax-voucher" className="underline text-primary">
                  How that one works
                </Link>
                .
              </p>
            </div>
          </div>

          <div className="mt-8 bg-primary rounded-[var(--radius)] px-7 py-6 flex flex-wrap items-baseline gap-x-4 gap-y-2">
            <span className="text-background text-lg font-semibold">
              Maximum off a qualifying cargo bike:
            </span>
            <span className="hand text-[48px] text-secondary leading-none">
              ${CO_MAX_CARGO_STACK.toLocaleString()}
            </span>
            <span className="text-[rgba(255,248,231,0.75)] text-sm">
              (rebate selection + income eligibility required for the first two)
            </span>
          </div>
        </div>
      </section>

      {/* ── How it works ── */}
      <section className="bg-primary py-20 relative overflow-hidden">
        <svg
          aria-hidden="true"
          className="absolute inset-0 w-full h-full pointer-events-none"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 1200 600"
          preserveAspectRatio="xMidYMid slice"
        >
          <path d="M0 100 Q300 60 600 120 T1200 80" fill="none" stroke="var(--secondary)" strokeWidth="1.2" opacity="0.12" />
          <path d="M0 260 Q250 220 500 280 T1000 240 T1200 270" fill="none" stroke="var(--secondary)" strokeWidth="0.8" opacity="0.12" />
          <path d="M0 420 Q350 380 700 440 T1200 400" fill="none" stroke="var(--secondary)" strokeWidth="1.2" opacity="0.12" />
        </svg>

        <div className="container relative">
          <Eyebrow className="text-secondary mb-3.5">How the rebate works · 4 steps</Eyebrow>
          <h2 className="text-3xl md:text-4xl font-bold text-background">
            Register first,{' '}
            <span className="hand text-[56px] text-secondary">shop second.</span>
          </h2>
          <p className="mt-4 text-[rgba(255,248,231,0.82)] max-w-2xl">
            The order matters. Rebates are not retroactive — a bike bought before you
            receive a rebate does not count.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-[22px] mt-9">
            {steps.map((step) => (
              <div
                key={step.hand}
                className="bg-[rgba(255,248,231,0.06)] border border-[rgba(255,215,0,0.3)] rounded-[var(--radius)] p-7"
              >
                <div className="hand text-[22px] text-secondary mb-3">{step.hand}</div>
                <h3 className="text-background text-xl mb-2.5 font-semibold">{step.t}</h3>
                <p className="text-[rgba(255,248,231,0.82)] text-[15px] leading-[1.55]">{step.d}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 flex flex-col sm:flex-row justify-center gap-4">
            <a href={CO_REBATE_PORTAL_URL} target="_blank" rel="noopener noreferrer">
              <PrimaryButton size="lg" invert hand="Register at the state portal" sub="Free · about two minutes" />
            </a>
            <Link href="/book">
              <PrimaryButton size="lg" invert hand="Book a test ride first" sub="Sixty days goes fast — know the bike" />
            </Link>
          </div>
        </div>
      </section>

      {/* ── Eligibility ── */}
      <section className="bg-[var(--background)] py-20">
        <div className="container">
          <Eyebrow className="text-accent">Who qualifies for the rebate</Eyebrow>
          <h2 className="mt-3 text-3xl md:text-4xl font-bold text-foreground">
            Two ways in —{' '}
            <span className="hand text-[52px] text-primary">income or a program.</span>
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-[22px] mt-9">
            <div className="bg-card border border-border rounded-[var(--radius)] p-7 shadow-[var(--shadow-sm)]">
              <div className="hand text-[52px] text-accent leading-none mb-3">01</div>
              <h3 className="text-xl font-semibold text-foreground mb-2">Household income</h3>
              <p className="text-sm text-foreground leading-relaxed">
                Below 80% of the area median income for your county. The state uses Line 9
                (Total Income) of your 2025 federal 1040 if you are not enrolled in a
                program below. Boulder County&apos;s threshold is published by the state —
                check the official page rather than guessing.
              </p>
            </div>

            <div className="bg-card border border-border rounded-[var(--radius)] p-7 shadow-[var(--shadow-sm)]">
              <div className="hand text-[52px] text-accent leading-none mb-3">02</div>
              <h3 className="text-xl font-semibold text-foreground mb-2">Or an assistance program</h3>
              <p className="text-sm text-foreground leading-relaxed mb-3">
                Enrollment in any one of these qualifies you outright — no income math:
              </p>
              <div className="flex flex-wrap gap-2">
                {eligibilityPrograms.map((p) => (
                  <span
                    key={p}
                    className="inline-flex items-center px-3 py-1 rounded-full bg-[var(--background-warm)] border border-border text-xs font-semibold text-primary"
                  >
                    {p}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <p className="mt-6 text-sm text-muted-foreground max-w-3xl">
            You must live in Colorado and be at least 18. Full eligibility rules,
            accepted documents, and the current AMI figures live on the{' '}
            <a
              href={CO_REBATE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="underline text-primary"
            >
              Colorado Energy Office rebate page
            </a>
            . If you are not income-qualified, the{' '}
            <a
              href={CO_TAX_CREDIT_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="underline text-primary"
            >
              ${CO_TAX_CREDIT} tax credit discount
            </a>{' '}
            is open to every Colorado resident.
          </p>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="bg-[var(--background-warm)] py-[72px]">
        <div className="container grid grid-cols-1 md:grid-cols-2 gap-12 items-start">
          {/* Deliberately NOT a PolaroidNote. That atom renders a "R" avatar and
              a "— Ryan" signoff, i.e. it attributes its text to Ryan. Ryan has
              not said any of this, and inventing first-person quotes for him is
              exactly what the 2026-05-27 zero-hallucination revision stripped out
              of this site. This is unattributed shop copy until Ryan sends his own
              words, at which point swap this block for a PolaroidNote. */}
          <aside
            className="rounded-[var(--radius)] border border-border bg-card p-7 shadow-[var(--shadow-sm)]"
            style={{ borderLeft: '4px solid var(--accent)' }}
          >
            <p className="hand text-[26px] leading-[1.3] text-foreground">
              Registering costs nothing and takes about two minutes. If the lottery goes
              your way you get sixty days to use it — so it&apos;s worth knowing which
              bike you want before then. Come ride a few while you wait to hear back.
            </p>
          </aside>

          <div>
            <Eyebrow className="text-accent mb-3">The fine print, in english</Eyebrow>
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-6">
              What people actually ask us.
            </h2>
            <div>
              {faqItems.map((item) => (
                <div key={item.q} className="py-3.5 border-b border-dashed border-[var(--border-strong)]">
                  <p className="font-semibold text-primary text-[15px] mb-1">{item.q}</p>
                  <p className="text-sm text-foreground leading-relaxed">{item.a}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  )
}

Rebate.layout = (page: ReactNode) => <Layout>{page}</Layout>

export default Rebate
